import JobCard2 from "@/components/card/jobCard2/jobCard2";
import useTrans from "@/hooks/useTrans";
import {
  getRecruitmentPlansOrCampaigns,
  purfifyHTML,
} from "@/services/services";
import variables from "@/styles/variables.module.scss";
import {
  Button,
  Center,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Modal,
  Select,
  Stack,
  Text,
  Pagination,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useRouter } from 'next/router'
import convert from "color-convert";
import parse from "html-react-parser";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft } from "tabler-icons-react";
import { useMediaQuery } from "usehooks-ts";
import Card1 from "../../card/card1/card1";
import JobCardCustom from "../../card/jobCardCustom/jobCardCustom";
import JobCardDetail from "../../card/jobCardDetail/jobCardDetail";
import { JobCardRect } from "../../card/jobCardRect/jobCardRect";
import _Pagination from "../../helpers/pagination";
import dynamicFormStyles from "components/formDynamic/styles/dynamic-form.module.scss";
import JobListStyles from "./jobList.module.scss";

const ModalContent = dynamic(() => import("@/components/modal/modalContent"), {
  ssr: false,
});

function JobList({
  options,
  url,
  primaryColor,
  secondaryColor,
  defaultImage,
  imageLogo,
  hideManagerSection,
  hideApplicationCode = false,
  showImage = true,
  messageSuccessNoCode,
}) {
  const trans = useTrans();
  const { locale } = useRouter()
  const [activePage, setActivePage] = useState(1);
  const [activeLimit, _setActiveLimit] = useState(options.limit || 15);
  const [paginateData, setPaginateData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [currentListType, setCurrentListType] = useState("recruitments");
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestRef = useRef(null);
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const modalWidthSize = 800;

  const isMobile = useMediaQuery("(max-width: 768px)");
  const isIpad = useMediaQuery("(max-width: 1000px)");

  const componentStyle = options.styles;

  const paginationWrapperStyle = {
    display: "flex",
    justifyContent: "center",
    cursor: isLoading ? "wait" : "default",
    paginationStyle: {
      item: {
        "&[data-active]": {
          backgroundColor: primaryColor,
        },
      },
      radius: "xl",
      boundaries: 1,
      siblings: 1,
    },
  };

  const listTypes = [
    { value: "recruitments", label: trans.recruitments },
    { value: "campaigns", label: trans.campaigns },
  ];

  const customLoader = (
    <svg
      width="54"
      height="54"
      viewBox="0 0 38 38"
      xmlns="http://www.w3.org/2000/svg"
      stroke={variables.primaryColor}
    >
      <g fill="none" fillRule="evenodd">
        <g transform="translate(1 1)" strokeWidth="2">
          <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
          <path d="M36 18c0-9.94-8.06-18-18-18">
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 18 18"
              to="360 18 18"
              dur="1s"
              repeatCount="indefinite"
            />
          </path>
        </g>
      </g>
    </svg>
  );

  const getRecruitmentsApi = async () => {
    setIsLoading(true);
    const requestId = Date.now();
    latestRequestRef.current = requestId;

    try {
      const res = await getRecruitmentPlansOrCampaigns(
        currentListType,
        url,
        options.query || "",
        options.jsonata,
        activePage,
        activeLimit,
        options.dynamicFilters,
      );
      if (latestRequestRef.current === requestId) {
        setPaginateData(res);
      }
    } catch (err) {
      if (latestRequestRef.current === requestId) {
        setPaginateData(null);
      }
    } finally {
      if (latestRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    getRecruitmentsApi();
  }, [activePage]);

  useEffect(() => {
    if (activePage !== 1) {
      setActivePage(1);
      return;
    }

    getRecruitmentsApi();
  }, [options.dynamicFilters, currentListType]);

  useEffect(() => {
    if (options.cardType === "job-card2") {
      if (detail && !modalOpened) {
        openModal();
      }
    }
  }, [detail, modalOpened, options]);

  const useDetail = options.useDetail === undefined || options.useDetail;

  let jobCardJSX = [];
  if (paginateData) {
    jobCardJSX = paginateData.data.map((recruitmentPlan, index) => {
      let cardJSX = <></>;

      switch (options.cardType) {
        case "job-card2":
          {
            cardJSX = (
              <JobCard2
                onBtnClick={() => {
                  if (useDetail) {
                    setDetail(recruitmentPlan);
                  } else {
                    const a = document.createElement("a");
                    a.target = "_blank";
                    a.href =
                      options.data +
                      `/${recruitmentPlan.id}` +
                      `?type_child=${options.child}${recruitmentPlan.isCampaign ? "&campaign=true" : ""
                      }`;
                    a.click();
                  }
                }}
                title={recruitmentPlan.name}
                image={recruitmentPlan.image || defaultImage || imageLogo}
                salary={recruitmentPlan.salary}
                location={recruitmentPlan.locationAlt}
                expirationDate={
                  recruitmentPlan.expiration_date ?? recruitmentPlan.date
                }
                tags={recruitmentPlan.tags}
                description={recruitmentPlan.description}
              />
            );
          }

          break;

        case "card1":
          cardJSX = (
            <Card1
              title={recruitmentPlan.name}
              image={recruitmentPlan.image || defaultImage || imageLogo}
              salary={recruitmentPlan.salary}
              location={recruitmentPlan.locationAlt}
              expirationDate={recruitmentPlan.date}
              tags={recruitmentPlan.tags}
              description={recruitmentPlan.description}
              defaultImage={defaultImage}
              hideImage={!showImage || detail || isMobile}
              showButton={options.showButton}
              shareUrl={"https://" + url + `/${options.data}/${recruitmentPlan.id}` +
                `?type_child=${options.child}${recruitmentPlan.isCampaign ? "&campaign=true" : ""
                }`}
              shareText={options.shareText?.[locale]}
              onBtnClick={(e) => {
                e.stopPropagation();

                const url =
                  options.data +
                  `/${recruitmentPlan.id}` +
                  `?type_child=${options.child}${recruitmentPlan.isCampaign ? "&campaign=true" : ""
                  }`;

                window.open(url, "_blank", "noopener,noreferrer");
              }}
            />
          );
          break;
        case "jobCardCustom":
          cardJSX = (
            <JobCardCustom
              recruitmentPlan={recruitmentPlan}
              showButton={options.showButton}
              cardStyle={options.cardStyle}
              buttonLabel={options.buttonLabel}
              onBtnClick={(e) => {
                e.stopPropagation();
                const url =
                  options.data +
                  `/${recruitmentPlan.id}` +
                  `?type_child=${options.child}${recruitmentPlan.isCampaign ? "&campaign=true" : ""
                  }`;

                window.open(url, options.openNewTab ? "_blank" : "_self", "noopener,noreferrer");
              }}
            />
          );
          break;
        default:
          {
            cardJSX = (
              <JobCardRect
                inDetailMode={!!detail}
                isSelected={
                  recruitmentPlan.id && recruitmentPlan.id === detail?.id
                }
                recruitmentPlan={recruitmentPlan}
                descriptionArray={options.descriptionArray}
              />
            );
          }
          break;
      }

      return (
        <div
          key={index}
          style={{
            cursor: "pointer",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
          onClick={() => {
            if (useDetail) {
              setDetail(recruitmentPlan);
            } else {
              const a = document.createElement("a");
              a.target = "_blank";
              a.href =
                options.data +
                `/${recruitmentPlan.id}` +
                `?type_child=${options.child}${recruitmentPlan.isCampaign ? "&campaign=true" : ""
                }`;
              a.click();
            }
          }}
        >
          {cardJSX}
        </div>
      );
    });
  }
  const convertColor = convert.hex.hsl(primaryColor);

  let titleJSX = <></>;

  switch (options.titleType) {
    case "alt1":
      {
        titleJSX = (
          <Group position="center">
            <div className={JobListStyles.titleAlt1}>
              {parse(purfifyHTML(options.title || ""))}
              <div
                className={JobListStyles.backgroundAlt1}
                style={{ backgroundColor: secondaryColor, width: "120%" }}
              ></div>
            </div>
          </Group>
        );
      }
      break;

    default:
      {
        titleJSX = (
          <Stack spacing={16}>
            <Flex
              w={"100%"}
              justify={"space-between"}
              direction={isMobile ? "column" : "row"}
              gap={20}
            >
              <Group spacing={16} align="center">
                <Stack>
                  <Group>
                    <Text className={JobListStyles.startTitle}>
                      {options.title || trans.jobListTitle}
                    </Text>
                    <Text className={JobListStyles.searchCountText}>
                      {`(${paginateData?.total ?? "0"} ${trans.job})`}
                    </Text>
                  </Group>
                  <div className={JobListStyles.divider} />
                </Stack>
              </Group>
              {options.hasCampaign === true && (
                <Select
                  className={dynamicFormStyles.formDynamicTextDateSelectInput}
                  data={listTypes}
                  value={currentListType}
                  defaultValue={currentListType}
                  onChange={setCurrentListType}
                  styles={(theme) => ({
                    item: {
                      "&[data-selected]": {
                        "&, &:hover": {
                          backgroundColor: variables.primaryColor,
                          color: theme.white,
                        },
                      },

                      "&[data-hovered]": {},
                    },
                  })}
                />
              )}
            </Flex>
          </Stack>
        );
      }
      break;
  }

  return (
    <>
      {detail && (
        <Modal
          overflow={"inside"}
          size={modalWidthSize}
          opened={modalOpened}
          onClose={() => {
            closeModal();
            setDetail(null);
          }}
          title={
            <Group spacing={5}>
              <Text fw={700} fz={16} c={primaryColor}>
                {trans.applyFor}
              </Text>
              <Text fw={700} fz={16} c={primaryColor}>
                {detail.name}
              </Text>
            </Group>
          }
        >
          <ModalContent
            display={"modal-content1"}
            contentWidthSize={modalWidthSize}
            url={url}
            channelType={detail.channelType}
            recruitmentPlanId={detail.id}
            isCampaign={detail.isCampaign}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            closeModal={closeModal}
            hideApplicationCode={hideApplicationCode}
            messageSuccessNoCode={messageSuccessNoCode}
          />
        </Modal>
      )}
      {detail && useDetail ? (
        <Stack className={JobListStyles.jobList}>
          <Group
            position="left"
            style={{ width: "100%", padding: 10, marginBottom: -10 }}
          >
            <Button
              bg={primaryColor}
              style={{ width: "fit-content" }}
              sx={{
                "&:hover": {
                  backgroundColor: variables.hoverColor,
                },
              }}
              onClick={() => {
                setDetail(null);
              }}
            >
              <ArrowLeft />
            </Button>
          </Group>
          <Group className={JobListStyles.listAndDetail} align="start">
            <Stack
              pos={"relative"}
              spacing={24}
              style={{ width: "40%" }}
              hidden={isMobile}
            >
              <LoadingOverlay
                visible={isLoading}
                overlayBlur={1}
                transitionDuration={200}
                opacity={0.7}
                loader={customLoader}
              />
              <Stack className={JobListStyles.list}>{jobCardJSX}</Stack>
              <Center
                pos={"relative"}
                position="center"
                align="center"
                style={{ cursor: isLoading ? "wait" : "default" }}
              >
                <LoadingOverlay
                  visible={isLoading}
                  overlayBlur={1}
                  transitionDuration={150}
                  opacity={0}
                  loader={<></>}
                />
                <_Pagination
                  activePage={activePage}
                  dataLength={paginateData.pageCount}
                  setActivePage={setActivePage}
                  primaryColor={primaryColor}
                />
              </Center>
            </Stack>

            <div
              style={{
                width: isMobile ? "100%" : "calc(60% - 32px)",
                height: "100%",
                padding: isMobile && 10,
              }}
            >
              {detail && (
                <JobCardDetail
                  backgroundColor={options.detailBackgroundColor}
                  headerImage={options.detailHeaderImage}
                  headerBackgroundColor={
                    !options.detailHeaderImage &&
                    `hsla(${convertColor[0]}, ${convertColor[1]}%, ${convertColor[2]}%, 0.1)`
                  }
                  onBtnClick={openModal}
                  recruitmentPlan={detail}
                  descriptionArray={options.descriptionArray}
                  primaryColor={primaryColor}
                  hideManagerSection={hideManagerSection}
                  hideApplicationCode={hideApplicationCode}
                />
              )}
            </div>
          </Group>
        </Stack>
      ) : (
        <Stack spacing={32}>
          {(options.hasTitle === undefined || options.hasTitle) && titleJSX}
          {(isLoading && jobCardJSX.length == 0) &&
            <div style={{ position: 'relative', height: 100 }}>
              <LoadingOverlay
                visible={isLoading}
                overlayBlur={0}
                transitionDuration={150}
                opacity={1}
                overlayColor="#00000000"
                loader={customLoader}
              />
            </div>
          }
          <div
            style={{
              position: "relative",
              height: options.height
                ? options.height *
                (isIpad
                  ? isMobile
                    ? options.multiplier?.height?.mobile || 1.4
                    : options.multiplier?.height?.ipad || 1.1
                  : 1)
                : "100%",
              overflowY: options.overflowY || "hidden",
              overflowX: "hidden",
            }}
          >
            <LoadingOverlay
              visible={isLoading}
              overlayBlur={10}
              transitionDuration={150}
              opacity={0.7}
              loader={customLoader}
            />
            <Grid columns={12}>
              {jobCardJSX.map((card, index) => {
                return (
                  <Grid.Col
                    key={index}
                    span={isIpad ? 12 : options.minSpan || 6}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {card}
                  </Grid.Col>
                );
              })}
            </Grid>
          </div>
          {paginateData && (
            <div
              style={{
                ...paginationWrapperStyle,
                ...(componentStyle?.paginationWrapperStyle || {}),
              }}
            >
              <LoadingOverlay
                visible={isLoading}
                overlayBlur={1}
                transitionDuration={150}
                opacity={0}
                loader={<></>}
              />
              <Pagination
                page={activePage}
                onChange={setActivePage}
                radius={
                  componentStyle?.paginationWrapperStyle?.paginationStyle
                    ?.radius ?? "xl"
                }
                boundaries={
                  componentStyle?.paginationWrapperStyle?.paginationStyle
                    ?.boundaries ?? 1
                }
                siblings={
                  componentStyle?.paginationWrapperStyle?.paginationStyle
                    ?.siblings ?? 1
                }
                total={paginateData.pageCount}
                styles={{
                  ...paginationWrapperStyle?.paginationStyle,
                  ...(componentStyle?.paginationWrapperStyle?.paginationStyle ||
                    {}),
                }}
              />
            </div>
          )}
        </Stack>
      )}
    </>
  );
}

export default JobList;
